import type { ReactNode } from 'react'
import { Component } from 'react'
import { AlertTriangle, RefreshCw, Home } from 'lucide-react'
import Button from '@/components/ui/Button'

interface Props {
  children: ReactNode
  fallback?: ReactNode
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void
}

interface State {
  hasError: boolean
  error: Error | null
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Unhandled error:', error, errorInfo)
    this.props.onError?.(error, errorInfo)
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null })
  }

  handleGoHome = () => {
    window.location.href = '/'
  }

  render() {
    if (this.state.hasError) {
      // Custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback
      }

      // Default error UI
      return (
        <div className='min-h-screen flex items-center justify-center bg-background px-4'>
          <div className='max-w-md w-full text-center'>
            <div className='inline-flex items-center justify-center w-16 h-16 rounded-full bg-destructive/10 mb-6'>
              <AlertTriangle className='w-8 h-8 text-destructive' />
            </div>

            <h1 className='text-2xl font-bold text-foreground mb-2'>
              Something went wrong
            </h1>

            <p className='text-muted-foreground mb-6'>
              We're sorry, but something unexpected happened. Please try
              refreshing the page or go back to the home page.
            </p>

            {/* Show error details in development */}
            {import.meta.env.DEV && this.state.error && (
              <details className='mb-6 text-left bg-muted/50 rounded-lg p-4'>
                <summary className='cursor-pointer font-medium text-sm mb-2'>
                  Error Details (Development Only)
                </summary>
                <pre className='text-xs overflow-auto text-destructive'>
                  {this.state.error.toString()}
                </pre>
              </details>
            )}

            <div className='flex flex-col sm:flex-row gap-3 justify-center'>
              <Button
                onClick={this.handleReset}
                variant='outline'
                className='inline-flex items-center gap-2'
              >
                <RefreshCw className='w-4 h-4' />
                Try Again
              </Button>

              <Button
                onClick={this.handleGoHome}
                className='inline-flex items-center gap-2'
              >
                <Home className='w-4 h-4' />
                Go Home
              </Button>
            </div>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}

/**
 * Route-specific Error Fallback Component
 * Use this with React Router's errorElement prop
 */
export function RouteErrorFallback() {
  const handleGoHome = () => {
    window.location.href = '/'
  }

  const handleGoBack = () => {
    window.history.back()
  }

  return (
    <div className='min-h-screen flex items-center justify-center bg-background px-4'>
      <div className='max-w-md w-full text-center'>
        <div className='inline-flex items-center justify-center w-16 h-16 rounded-full bg-destructive/10 mb-6'>
          <AlertTriangle className='w-8 h-8 text-destructive' />
        </div>

        <h1 className='text-2xl font-bold text-foreground mb-2'>Page Error</h1>

        <p className='text-muted-foreground mb-6'>
          This page encountered an error. Please try going back or return to the
          home page.
        </p>

        <div className='flex flex-col sm:flex-row gap-3 justify-center'>
          <Button
            onClick={handleGoBack}
            variant='outline'
            className='inline-flex items-center gap-2'
          >
            <RefreshCw className='w-4 h-4' />
            Go Back
          </Button>

          <Button
            onClick={handleGoHome}
            className='inline-flex items-center gap-2'
          >
            <Home className='w-4 h-4' />
            Go Home
          </Button>
        </div>
      </div>
    </div>
  )
}
